/**
 * B72(b) — light/dark theming as a @transact/ui capability.
 *
 * The decision is pure, and since B403 measured-pass finding #1
 * (2026-08-25, design-side §10 ruling — Pathik can overrule) it is
 * STORED-ONLY on every surface: a stored choice wins; absent one the
 * face is DARK, whatever the OS prefers. The persisted toggle is the
 * only path to light — OS preference alone never flips it. (B368 (e)'s
 * os-light-wins rule survives as an explicit policy arm, solely as the
 * ruling's reversal path.)
 * Application is ONE attribute on a root element — data-transact-theme —
 * which tokens.css keys the dark set off, so any subtree can be themed
 * independently (the gallery's side-by-side pair).
 *
 * Composition with the §9.2 embed theme: host hints arrive as INLINE
 * custom properties (element style), which by CSS precedence beat both
 * theme blocks — a tenant's brand always wins inside its iframe.
 */
import { useCallback, useEffect, useState } from "react";

export type TransactTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "transact-theme";

/**
 * B403 measured-pass finding #1 (2026-08-25) — the boot policy. Every
 * exemplar draws the dark face and Pathik's storefront ruling already said
 * "the exemplar's darkness is not tenant-themeable"; the measured pass
 * caught the admin booting the LIGHT sheet under a headless prefers-light.
 * So `stored-only` is the default on EVERY surface: the persisted toggle
 * is the only path to light, and the OS preference is never consulted.
 * (This subsumes B397's storefront-only `"dark"` policy — the hosted
 * storefront now just boots the default.) `os-light-wins` is retained
 * ONLY as the ruling's reversal path — no surface passes it today.
 */
export type ThemePolicy = "stored-only" | "os-light-wins";

/** Stored choice wins; absent one, DARK (stored-only, B403 finding #1). */
export function resolveTheme(
  stored: string | null,
  prefersLight: boolean,
  policy: ThemePolicy = "stored-only",
): TransactTheme {
  if (stored === "dark" || stored === "light") return stored;
  if (policy === "os-light-wins") return prefersLight ? "light" : "dark";
  return "dark";
}

/** The surface's policy, set once at boot; the toggle hook reads the same one. */
let bootPolicy: ThemePolicy = "stored-only";

/** One attribute on whatever root is handed in — DOM, fake, or subtree. */
export function applyTheme(
  root: { setAttribute(name: string, value: string): void },
  theme: TransactTheme,
): void {
  root.setAttribute("data-transact-theme", theme);
}

function initialTheme(): TransactTheme {
  if (typeof window === "undefined") return "dark"; // B368 (e): dark absent any preference
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    stored = null; // storage refused (private mode, embedded 3p context) — preference decides
  }
  const prefersLight =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: light)").matches;
  return resolveTheme(stored, prefersLight, bootPolicy);
}

/**
 * B368 (e) — the BOOT half the toggle could never carry: the hook lives
 * inside ThemeToggle, which mounts only after sign-in, so every pre-auth
 * screen (the B366 door first among them) rendered the sheet's light
 * default whatever the operator chose. Surfaces call this once in
 * main.tsx, BEFORE first render, so the first paint is already the
 * resolved theme; the toggle's own hook then agrees because both read
 * the same derivation.
 */
export function bootTransactTheme(policy?: ThemePolicy): void {
  if (policy !== undefined) bootPolicy = policy;
  if (typeof document === "undefined") return;
  applyTheme(document.documentElement, initialTheme());
}

/**
 * The one wiring every surface uses: current theme + a toggle that
 * persists the choice and repaints the document root.
 */
export function useTransactTheme(): readonly [TransactTheme, () => void] {
  const [theme, setTheme] = useState<TransactTheme>(initialTheme);
  useEffect(() => {
    applyTheme(document.documentElement, theme);
  }, [theme]);
  const toggle = useCallback(() => {
    setTheme((current) => {
      const next: TransactTheme = current === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        /* choice simply won't persist */
      }
      return next;
    });
  }, []);
  return [theme, toggle] as const;
}
